import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import { ArrowLeft, Plane, Hotel, Search, Calendar, Users, LogIn } from 'lucide-react';
import { toast } from 'sonner';

const TravelSearch = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [searchType, setSearchType] = useState('flights');
  const [flightForm, setFlightForm] = useState({
    origin: 'New York (JFK)',
    destination: 'Lagos (LOS)',
    date: '',
    passengers: 1
  });
  const [hotelForm, setHotelForm] = useState({
    location: 'Lagos, Nigeria',
    checkin: '',
    checkout: '',
    guests: 1
  });
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const searchFlights = async () => {
    setSearching(true);
    try {
      const params = new URLSearchParams(flightForm);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/travel/flights/search?${params}`);
      const data = await response.json();
      setSearchResults(data.flights || []);
    } catch (error) {
      toast.error('Error searching flights');
    } finally {
      setSearching(false);
    }
  };

  const searchHotels = async () => {
    setSearching(true);
    try {
      const params = new URLSearchParams(hotelForm);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/travel/hotels/search?${params}`);
      const data = await response.json();
      setSearchResults(data.hotels || []);
    } catch (error) {
      toast.error('Error searching hotels');
    } finally {
      setSearching(false);
    }
  };

  const bookItem = async (item) => {
    // Check if user is logged in, if not show login prompt
    if (!user) {
      toast.error('Please login to complete your booking');
      setTimeout(() => {
        navigate('/register', { state: { returnTo: '/travel' } });
      }, 1500);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const bookingData = {
        booking_type: searchType,
        details: {
          ...item,
          amount: item.price_usd || item.price_per_night_usd,
          currency: 'USD'
        }
      };
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/travel/bookings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bookingData)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Booking confirmed! Reference: ${data.booking.booking_reference}. You earned ${data.points_earned} loyalty points!`);
        navigate('/dashboard');
      } else {
        toast.error('Booking failed');
      }
    } catch (error) {
      toast.error('Error creating booking');
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] p-8" data-testid="travel-search-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(user ? '/dashboard' : '/')}
            className="flex items-center gap-2 text-white/60 hover:text-white mb-4 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span style={{ fontFamily: 'Manrope' }}>Back to {user ? 'Dashboard' : 'Home'}</span>
          </button>
          <h1 className="text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Sora' }}>Travel Booking</h1>
          <p className="text-white/60" style={{ fontFamily: 'Manrope' }}>Search flights and hotels for your journey</p>
        </div>

        {/* Guest Browse Notice */}
        {!user && (
          <div className="mb-6 p-4 bg-[#FFD60A]/10 border border-[#FFD60A]/30 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <LogIn className="w-5 h-5 text-[#FFD60A]" />
              <p className="text-white/80" style={{ fontFamily: 'Manrope' }}>
                Browsing as guest. <span className="text-[#FFD60A] font-semibold">Login required</span> to complete booking.
              </p>
            </div>
            <button
              onClick={() => navigate('/login')}
              className="btn-primary text-sm px-6 py-2"
              data-testid="guest-login-btn"
            >
              Login
            </button>
          </div>
        )}

        {/* Search Type Tabs */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => { setSearchType('flights'); setSearchResults([]); }}
            className={`flex items-center gap-2 px-6 py-3 transition-all duration-300 ${
              searchType === 'flights'
                ? 'bg-[#FFD60A] text-black font-semibold'
                : 'bg-white/5 text-white/60 hover:bg-white/10'
            }`}
            data-testid="flights-tab"
          >
            <Plane className="w-5 h-5" />
            <span style={{ fontFamily: 'Manrope' }}>Flights</span>
          </button>
          <button
            onClick={() => { setSearchType('hotels'); setSearchResults([]); }}
            className={`flex items-center gap-2 px-6 py-3 transition-all duration-300 ${
              searchType === 'hotels'
                ? 'bg-[#FFD60A] text-black font-semibold'
                : 'bg-white/5 text-white/60 hover:bg-white/10'
            }`}
            data-testid="hotels-tab"
          >
            <Hotel className="w-5 h-5" />
            <span style={{ fontFamily: 'Manrope' }}>Hotels</span>
          </button>
        </div>

        {/* Search Form */}
        <div className="card-glass p-8 mb-8">
          {searchType === 'flights' ? (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-white/80 text-sm mb-2" style={{ fontFamily: 'Manrope' }}>Origin</label>
                <input
                  type="text"
                  className="input-base w-full"
                  value={flightForm.origin}
                  onChange={(e) => setFlightForm({ ...flightForm, origin: e.target.value })}
                  data-testid="flight-origin-input"
                />
              </div>
              <div>
                <label className="block text-white/80 text-sm mb-2" style={{ fontFamily: 'Manrope' }}>Destination</label>
                <input
                  type="text"
                  className="input-base w-full"
                  value={flightForm.destination}
                  onChange={(e) => setFlightForm({ ...flightForm, destination: e.target.value })}
                  data-testid="flight-destination-input"
                />
              </div>
              <div>
                <label className="block text-white/80 text-sm mb-2" style={{ fontFamily: 'Manrope' }}>Date</label>
                <input
                  type="date"
                  className="input-base w-full"
                  value={flightForm.date}
                  onChange={(e) => setFlightForm({ ...flightForm, date: e.target.value })}
                  data-testid="flight-date-input"
                />
              </div>
              <div>
                <label className="block text-white/80 text-sm mb-2" style={{ fontFamily: 'Manrope' }}>Passengers</label>
                <input
                  type="number"
                  min="1"
                  className="input-base w-full"
                  value={flightForm.passengers}
                  onChange={(e) => setFlightForm({ ...flightForm, passengers: parseInt(e.target.value) })}
                  data-testid="flight-passengers-input"
                />
              </div>
              <button
                onClick={searchFlights}
                disabled={searching}
                className="btn-primary md:col-span-4"
                data-testid="search-flights-btn"
              >
                <Search className="w-5 h-5 inline-block mr-2" />
                {searching ? 'Searching...' : 'Search Flights'}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-white/80 text-sm mb-2" style={{ fontFamily: 'Manrope' }}>Location</label>
                <input
                  type="text"
                  className="input-base w-full"
                  value={hotelForm.location}
                  onChange={(e) => setHotelForm({ ...hotelForm, location: e.target.value })}
                  data-testid="hotel-location-input"
                />
              </div>
              <div>
                <label className="block text-white/80 text-sm mb-2" style={{ fontFamily: 'Manrope' }}>Check-in</label>
                <input
                  type="date"
                  className="input-base w-full"
                  value={hotelForm.checkin}
                  onChange={(e) => setHotelForm({ ...hotelForm, checkin: e.target.value })}
                  data-testid="hotel-checkin-input"
                />
              </div>
              <div>
                <label className="block text-white/80 text-sm mb-2" style={{ fontFamily: 'Manrope' }}>Check-out</label>
                <input
                  type="date"
                  className="input-base w-full"
                  value={hotelForm.checkout}
                  onChange={(e) => setHotelForm({ ...hotelForm, checkout: e.target.value })}
                  data-testid="hotel-checkout-input"
                />
              </div>
              <div>
                <label className="block text-white/80 text-sm mb-2" style={{ fontFamily: 'Manrope' }}>Guests</label>
                <input
                  type="number"
                  min="1"
                  className="input-base w-full"
                  value={hotelForm.guests}
                  onChange={(e) => setHotelForm({ ...hotelForm, guests: parseInt(e.target.value) })}
                  data-testid="hotel-guests-input"
                />
              </div>
              <button
                onClick={searchHotels}
                disabled={searching}
                className="btn-primary md:col-span-4"
                data-testid="search-hotels-btn"
              >
                <Search className="w-5 h-5 inline-block mr-2" />
                {searching ? 'Searching...' : 'Search Hotels'}
              </button>
            </div>
          )}
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div>
            <h2 className="text-3xl font-bold text-white mb-6" style={{ fontFamily: 'Sora' }}>Search Results</h2>
            <div className="grid grid-cols-1 gap-6">
              {searchType === 'flights' ? (
                searchResults.map((flight) => (
                  <div key={flight.id} className="card-glass p-6" data-testid={`flight-${flight.flight_number}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Sora' }}>{flight.airline}</h3>
                        <p className="text-white/60 mb-2" style={{ fontFamily: 'Manrope' }}>Flight {flight.flight_number}</p>
                        <div className="flex items-center gap-4 text-sm text-white/80">
                          <span>{flight.departure_time}</span>
                          <span>→</span>
                          <span>{flight.arrival_time}</span>
                          <span className="text-white/40">({flight.duration})</span>
                        </div>
                        <p className="text-white/40 text-sm mt-2">{flight.stops === 0 ? 'Non-stop' : `${flight.stops} stop(s)`}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-[#FFD60A] mb-2" style={{ fontFamily: 'Sora' }}>${flight.price_usd.toFixed(2)}</div>
                        <p className="text-white/40 text-sm mb-4">{flight.available_seats} seats available</p>
                        <button
                          onClick={() => bookItem(flight)}
                          className="btn-primary"
                          data-testid={`book-flight-${flight.flight_number}`}
                        >
                          Book Now
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                searchResults.map((hotel) => (
                  <div key={hotel.id} className="card-glass p-6 flex gap-6" data-testid={`hotel-${hotel.name.replace(/\s+/g, '-')}`}>
                    <img
                      src={hotel.image}
                      alt={hotel.name}
                      className="w-48 h-32 object-cover"
                    />
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Sora' }}>{hotel.name}</h3>
                      <p className="text-white/60 mb-2" style={{ fontFamily: 'Manrope' }}>{hotel.location}</p>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-[#FFD60A] text-lg font-bold">★ {hotel.rating}</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {hotel.amenities.map((amenity, index) => (
                          <span key={index} className="px-3 py-1 bg-white/5 text-white/60 text-xs">{amenity}</span>
                        ))}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-[#FFD60A] mb-1" style={{ fontFamily: 'Sora' }}>${hotel.price_per_night_usd.toFixed(2)}</div>
                      <p className="text-white/40 text-sm mb-4">per night</p>
                      <button
                        onClick={() => bookItem(hotel)}
                        className="btn-primary"
                        data-testid={`book-hotel-${hotel.name.replace(/\s+/g, '-')}`}
                      >
                        Book Now
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TravelSearch;